import {
  isPolyClosed,
  type CanvasFeature,
  type CircleFeature,
  type IconFeature,
  type PathFeature,
  type PolyFeature,
  type TextFeature,
  type Transformation,
} from '@pn/core/domain/drawing';
import type { Point } from '@pn/core/domain/point';
import { assert } from '@pn/core/utils/assert';
import type { DrawingState } from '@pn/services/drawing';
import {
  RESIZE_HANDLE_SIZE,
  SELECT_STROKE_WIDTH,
  computeCanvasTransformation,
  drawBoxSelection,
  drawResizeHandle,
  drawVertexHandle,
  formatArea,
  formatDistance,
  getCanvasBBox,
} from '@pn/services/drawing';
import { isTransparent } from '@pn/services/utils/color';
import { pnFontFamily } from '@pn/ui/theme/themes/shared';
import { isNil } from 'lodash-es';

export function getContext(
  canvas: HTMLCanvasElement | null | undefined
): CanvasRenderingContext2D {
  const ctx = canvas?.getContext('2d');
  assert(ctx, 'Canvas context not found');
  return ctx;
}

export function drawStatic(params: {
  ctx: CanvasRenderingContext2D;
  drawingState: DrawingState;
  transformation: Transformation;
}): void {
  const { ctx, drawingState, transformation } = params;

  ctx.save();

  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  ctx.scale(transformation.scale, transformation.scale);
  ctx.translate(transformation.dx, transformation.dy);

  for (const id of drawingState.order) {
    const feature = drawingState.features[id];
    if (!feature.isVisible) continue;
    drawingState.paths[id] = drawFeature(ctx, feature);
  }

  ctx.restore();
}

export function drawLive(params: {
  ctx: CanvasRenderingContext2D;
  drawingState: DrawingState;
  transformation: Transformation;
}): void {
  const { ctx, drawingState, transformation } = params;

  const boxSelection = drawingState.boxSelection;
  const featuresSelectedArr = Object.values(drawingState.featuresSelected);
  const isSingleSelection = featuresSelectedArr.length === 1;

  ctx.save();

  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  ctx.scale(transformation.scale, transformation.scale);
  ctx.translate(transformation.dx, transformation.dy);

  if (!isNil(boxSelection))
    drawBoxSelection({ ctx, bbox: boxSelection, transformation });

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  featuresSelectedArr.forEach((feature) => {
    const bbox = getCanvasBBox(feature);

    const adjustedX = bbox.x - bbox.strokeWidth / 2;
    const adjustedY = bbox.y - bbox.strokeWidth / 2;
    const adjustedWidth = bbox.width + bbox.strokeWidth;
    const adjustedHeight = bbox.height + bbox.strokeWidth;

    minX = Math.min(minX, adjustedX);
    minY = Math.min(minY, adjustedY);
    maxX = Math.max(maxX, adjustedX + adjustedWidth);
    maxY = Math.max(maxY, adjustedY + adjustedHeight);

    if (isSingleSelection) return;

    /**
     * Thin outline around every multi-selected feature.
     * Not drawn in single selection mode.
     */
    ctx.lineWidth = 1 / transformation.scale;
    const dashOffset = 6 / transformation.scale;
    ctx.setLineDash([dashOffset, dashOffset]);

    ctx.strokeStyle = 'black';
    ctx.strokeRect(
      bbox.x - bbox.strokeWidth / 2,
      bbox.y - bbox.strokeWidth / 2,
      bbox.width + bbox.strokeWidth,
      bbox.height + bbox.strokeWidth
    );

    ctx.lineDashOffset = dashOffset; // offset to align white dashes with gaps
    ctx.strokeStyle = 'white';
    ctx.strokeRect(
      bbox.x - bbox.strokeWidth / 2,
      bbox.y - bbox.strokeWidth / 2,
      bbox.width + bbox.strokeWidth,
      bbox.height + bbox.strokeWidth
    );

    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;
  });

  const containerBBox = {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    strokeWidth: 2 / transformation.scale,
  };

  if (isFinite(containerBBox.x)) {
    ctx.lineWidth = SELECT_STROKE_WIDTH / transformation.scale;
    const dashOffset = 6 / transformation.scale;
    ctx.setLineDash([dashOffset, dashOffset]);

    ctx.strokeStyle = 'black';
    ctx.strokeRect(
      containerBBox.x - containerBBox.strokeWidth / 2,
      containerBBox.y - containerBBox.strokeWidth / 2,
      containerBBox.width + containerBBox.strokeWidth,
      containerBBox.height + containerBBox.strokeWidth
    );

    ctx.lineDashOffset = dashOffset; // offset to align white dashes with gaps
    ctx.strokeStyle = 'white';
    ctx.strokeRect(
      containerBBox.x - containerBBox.strokeWidth / 2,
      containerBBox.y - containerBBox.strokeWidth / 2,
      containerBBox.width + containerBBox.strokeWidth,
      containerBBox.height + containerBBox.strokeWidth
    );

    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;

    const offset =
      containerBBox.strokeWidth / 2 + RESIZE_HANDLE_SIZE / transformation.scale;

    const xMin = containerBBox.x - offset;
    const xMax = containerBBox.x + containerBBox.width + offset;
    const yMin = containerBBox.y - offset;
    const yMax = containerBBox.y + containerBBox.height + offset;

    drawResizeHandle({ ctx, x: xMin, y: yMin, transformation });
    drawResizeHandle({ ctx, x: xMax, y: yMin, transformation });
    drawResizeHandle({ ctx, x: xMin, y: yMax, transformation });
    drawResizeHandle({ ctx, x: xMax, y: yMax, transformation });
  }

  const featureSelected = isSingleSelection
    ? featuresSelectedArr[0]
    : undefined;
  if (featureSelected?.type === 'poly' && isNil(featureSelected.subType)) {
    featureSelected.coordinates.forEach((point) =>
      drawVertexHandle({ ctx, x: point.x, y: point.y, transformation })
    );
  }

  ctx.restore();
}

export function drawFeature(
  ctx: CanvasRenderingContext2D,
  feature: CanvasFeature
): Path2D {
  switch (feature.type) {
    case 'path':
      return drawPath(ctx, feature);
    case 'poly':
      return drawPoly(ctx, feature);
    case 'circle':
      return drawCircle(ctx, feature);
    case 'text':
      return drawText(ctx, feature);
    case 'icon':
      return drawIcon(ctx, feature);
  }
}

function drawPath(ctx: CanvasRenderingContext2D, feature: PathFeature): Path2D {
  const path = new Path2D(feature.pathData);
  ctx.strokeStyle = feature.strokeColor;
  ctx.lineWidth = feature.strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.globalAlpha = feature.opacity;
  ctx.stroke(path);

  ctx.globalAlpha = 1;

  return path;
}

function hasSharpEdges(feature: PolyFeature): boolean {
  return ['rectangle', 'rectangle_selection'].includes(feature.subType ?? '');
}

function drawPoly(ctx: CanvasRenderingContext2D, feature: PolyFeature): Path2D {
  const path = new Path2D();
  feature.coordinates.forEach((pt, index) =>
    index === 0 ? path.moveTo(pt.x, pt.y) : path.lineTo(pt.x, pt.y)
  );
  if (isPolyClosed(feature)) path.closePath();

  /**
   * Use off-screen canvas to fill the polygon with its stroke overlayed and
   * apply the result to the main canvas.
   */
  const workCanvas = getWorkCanvas();
  const workCtx = getWorkCtx();

  workCanvas.width = ctx.canvas.width;
  workCanvas.height = ctx.canvas.height;

  const { scale, dx, dy } = computeCanvasTransformation(ctx);

  workCtx.save();

  workCtx.scale(scale, scale);
  workCtx.translate(dx, dy);

  if (!isTransparent(feature.fillColor) && isPolyClosed(feature)) {
    workCtx.fillStyle = feature.fillColor;
    workCtx.fill(path);
  }

  if (feature.strokeWidth > 0) {
    workCtx.strokeStyle = feature.strokeColor;
    workCtx.lineWidth = feature.strokeWidth;
    workCtx.lineCap = hasSharpEdges(feature) ? 'butt' : 'round';
    workCtx.lineJoin = hasSharpEdges(feature) ? 'miter' : 'round';
    workCtx.stroke(path);
  }

  workCtx.restore();

  ctx.globalAlpha = feature.opacity;

  ctx.drawImage(
    workCanvas,
    -dx,
    -dy,
    ctx.canvas.width / scale,
    ctx.canvas.height / scale
  );

  const length = feature.coordinates.length;

  if (feature.arrow && length > 1) {
    const start = feature.coordinates[length - 2];
    const end = feature.coordinates[length - 1];

    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const arrowLength = Math.min(
      feature.strokeWidth * 4,
      Math.sqrt((end.y - start.y) ** 2 + (end.x - start.x) ** 2) * 0.33
    );
    const arrowAngle = Math.PI / 6;

    const arrow1 = {
      x: end.x - arrowLength * Math.cos(angle - arrowAngle),
      y: end.y - arrowLength * Math.sin(angle - arrowAngle),
    };
    const arrow2 = {
      x: end.x - arrowLength * Math.cos(angle + arrowAngle),
      y: end.y - arrowLength * Math.sin(angle + arrowAngle),
    };

    ctx.strokeStyle = feature.strokeColor;
    ctx.lineWidth = feature.strokeWidth;

    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(arrow1.x, arrow1.y);
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(arrow2.x, arrow2.y);
    ctx.stroke();
  }

  if (!isNil(feature.measurements)) {
    const { metric, imperial } = isNil(feature.measurements.area)
      ? formatDistance(feature.measurements.distance)
      : formatArea(feature.measurements.area);

    drawMeasurementBox({
      ctx,
      text: metric + '\n' + imperial,
      position: feature.measurements.position,
      fontSize: feature.measurements.fontSize,
    });
  }

  ctx.globalAlpha = 1;

  return path;
}

function drawCircle(
  ctx: CanvasRenderingContext2D,
  feature: CircleFeature
): Path2D {
  const path = new Path2D();
  path.arc(feature.center.x, feature.center.y, feature.radius, 0, 2 * Math.PI);

  /**
   * Use off-screen canvas to fill the circle with its stroke overlayed and
   * apply the result to the main canvas.
   */
  const workCanvas = getWorkCanvas();
  const workCtx = getWorkCtx();

  workCanvas.width = ctx.canvas.width;
  workCanvas.height = ctx.canvas.height;

  const { scale, dx, dy } = computeCanvasTransformation(ctx);

  workCtx.save();

  workCtx.scale(scale, scale);
  workCtx.translate(dx, dy);

  if (!isTransparent(feature.fillColor)) {
    workCtx.fillStyle = feature.fillColor;
    workCtx.fill(path);
  }

  if (feature.strokeWidth > 0) {
    workCtx.strokeStyle = feature.strokeColor;
    workCtx.lineWidth = feature.strokeWidth;
    workCtx.stroke(path);
  }

  workCtx.restore();

  ctx.globalAlpha = feature.opacity;

  ctx.drawImage(
    workCanvas,
    -dx,
    -dy,
    ctx.canvas.width / scale,
    ctx.canvas.height / scale
  );

  if (!isNil(feature.measurements)) {
    const { metric, imperial } = formatDistance(feature.measurements.radius);

    drawMeasurementBox({
      ctx,
      text: metric + '\n' + imperial,
      position: feature.measurements.position,
      fontSize: feature.measurements.fontSize,
    });
  }

  ctx.globalAlpha = 1;

  return path;
}

function drawText(ctx: CanvasRenderingContext2D, feature: TextFeature): Path2D {
  const x = feature.position.x;
  const initialY = feature.position.y + feature.fontSize * (1 - 0.05);

  ctx.font = `${feature.fontSize}px ${feature.fontFamily}`;
  ctx.textAlign = 'start';
  ctx.fillStyle = feature.textColor;
  ctx.globalAlpha = feature.opacity;

  const lines = feature.text.split('\n');
  const lineHeight = feature.fontSize * 1.2;

  lines.forEach((line, index) => {
    const y = initialY + index * lineHeight;
    ctx.fillText(line, x, y);
  });

  ctx.globalAlpha = 1;

  /* Create a Path2D object with the text bounding box */
  const path = new Path2D();

  const textMetrics = lines.map((line) => ctx.measureText(line));
  const first = textMetrics[0];
  const last = textMetrics[textMetrics.length - 1];
  const textHeight =
    (lines.length - 1) * lineHeight +
    first.actualBoundingBoxAscent +
    last.actualBoundingBoxDescent;
  const textWidth = Math.max(...textMetrics.map((metrics) => metrics.width));

  path.rect(x, initialY - first.actualBoundingBoxAscent, textWidth, textHeight);

  return path;
}

function drawIcon(ctx: CanvasRenderingContext2D, feature: IconFeature): Path2D {
  // viewBox is of form "minX minY width height", e.g. "0 0 24 24" or "10 -5 40 30"
  const [viewBoxX, viewBoxY, viewBoxW, viewBoxH] = feature.icon.viewBox
    .split(/\s+/)
    .map(Number);

  // Create the Path2D from the SVG pathData
  const path = new Path2D(feature.icon.pathData);

  // Calculate how much we scale to make the SVG's height = iconSize
  const scaleFactor = feature.iconSize / viewBoxH;

  ctx.save();

  // Move our drawing origin to the icon's center
  ctx.translate(feature.position.x, feature.position.y);

  // Shift the bounding box so its center aligns to the origin (0,0)
  ctx.translate(
    -(viewBoxX + viewBoxW / 2) * scaleFactor,
    -(viewBoxY + viewBoxH / 2) * scaleFactor
  );

  // Now scale the coordinate system so the final icon height = iconSize
  ctx.scale(scaleFactor, scaleFactor);

  ctx.fillStyle = feature.iconColor;
  ctx.globalAlpha = feature.opacity;

  ctx.fill(path);

  ctx.restore();

  /* To support hover detection, the returned path has to be untransformed */
  const bbox = getCanvasBBox(feature);
  const boundingPath = new Path2D();
  boundingPath.rect(bbox.x, bbox.y, bbox.width, bbox.height);

  return boundingPath;
}

function drawMeasurementBox(params: {
  ctx: CanvasRenderingContext2D;
  text: string;
  position: Point;
  fontSize: number;
}): void {
  const { ctx, text, position, fontSize } = params;

  ctx.font = `${fontSize}px ${pnFontFamily}`;
  ctx.textAlign = 'center';

  const lines = text.split('\n');
  const lineHeight = fontSize * 1.2;

  const textMetrics = lines.map((line) => ctx.measureText(line));
  const textHeight = lines.length * lineHeight;
  const textWidth = Math.max(...textMetrics.map((metrics) => metrics.width));

  const padding = fontSize * 0.5;
  const radius = fontSize * 0.5;
  const yOffset = lines.length * lineHeight * 1;
  const rectX = position.x - textWidth / 2 - padding;
  const rectY = position.y - yOffset - textHeight / 2 - padding / 2;
  const rectWidth = textWidth + 2 * padding;
  const rectHeight = textHeight + padding;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.beginPath();
  ctx.moveTo(rectX + radius, rectY);
  ctx.lineTo(rectX + rectWidth - radius, rectY);
  ctx.quadraticCurveTo(
    rectX + rectWidth,
    rectY,
    rectX + rectWidth,
    rectY + radius
  );
  ctx.lineTo(rectX + rectWidth, rectY + rectHeight - radius);
  ctx.quadraticCurveTo(
    rectX + rectWidth,
    rectY + rectHeight,
    rectX + rectWidth - radius,
    rectY + rectHeight
  );
  ctx.lineTo(rectX + radius, rectY + rectHeight);
  ctx.quadraticCurveTo(
    rectX,
    rectY + rectHeight,
    rectX,
    rectY + rectHeight - radius
  );
  ctx.lineTo(rectX, rectY + radius);
  ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'black';

  const initialY = position.y - yOffset - fontSize * 0.25;
  lines.forEach((line, index) => {
    const y = initialY + index * lineHeight;
    ctx.fillText(line, position.x, y);
  });
}

let workCanvas: HTMLCanvasElement | null = null;
let workCtx: CanvasRenderingContext2D | null = null;

function getWorkCanvas(): HTMLCanvasElement {
  if (isNil(workCanvas)) {
    workCanvas = document.createElement('canvas');
    workCtx = workCanvas.getContext('2d');
  }

  return workCanvas;
}

function getWorkCtx(): CanvasRenderingContext2D {
  assert(workCtx, 'Work context not found');
  return workCtx;
}
